import { z } from 'zod';
import { TiaRecommendedFlowSchema } from './test-impact';

/** VCS hosts that can list files changed at a SHA. Distinct from PR-preview `VCS_PROVIDERS`. */
export const SOURCE_REPO_PROVIDERS = ['github', 'gitlab', 'bitbucket', 'azure_devops'] as const;
export type SourceRepoProvider = (typeof SOURCE_REPO_PROVIDERS)[number];

export const SOURCE_REPO_KINDS = ['ui', 'api', 'service', 'infra', 'other'] as const;
export type SourceRepoKind = (typeof SOURCE_REPO_KINDS)[number];

import { SOURCE_REPO_ACCESS_MODES } from './execution-jobs';

export const ProjectSourceRepoSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  provider: z.enum(SOURCE_REPO_PROVIDERS),
  host: z.string().min(1).max(200),
  owner: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  kind: z.enum(SOURCE_REPO_KINDS),
  defaultBranch: z.string().min(1).max(200),
  accessMode: z.enum(SOURCE_REPO_ACCESS_MODES).default('saas_oauth'),
  /** Set when VCS access was successfully verified (create or verify endpoint). */
  accessVerifiedAt: z.string().nullable().optional(),
  accessVerifyError: z.string().nullable().optional(),
  /** Set when Analyze Application completed successfully for this repo. */
  lastAnalyzedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectSourceRepo = z.infer<typeof ProjectSourceRepoSchema>;

export const ListSourceReposResponseSchema = z.object({
  projectId: z.string().uuid(),
  repos: z.array(ProjectSourceRepoSchema),
});
export type ListSourceReposResponse = z.infer<typeof ListSourceReposResponseSchema>;

export const CreateSourceRepoRequestSchema = z.object({
  provider: z.enum(SOURCE_REPO_PROVIDERS),
  host: z.string().min(1).max(200).optional(),
  owner: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  kind: z.enum(SOURCE_REPO_KINDS).default('other'),
  defaultBranch: z.string().min(1).max(200).optional(),
  accessMode: z.enum(SOURCE_REPO_ACCESS_MODES).default('saas_oauth'),
});
export type CreateSourceRepoRequest = z.infer<typeof CreateSourceRepoRequestSchema>;

export const CreateSourceRepoResponseSchema = z.object({
  repo: ProjectSourceRepoSchema,
});
export type CreateSourceRepoResponse = z.infer<typeof CreateSourceRepoResponseSchema>;

export const CompareCommitRequestSchema = z.object({
  sha: z
    .string()
    .min(7)
    .max(64)
    .regex(/^[0-9a-fA-F]+$/, 'commit SHA must be hex'),
  repoId: z.string().uuid().optional(),
});
export type CompareCommitRequest = z.infer<typeof CompareCommitRequestSchema>;

export const CompareCommitResponseSchema = z.object({
  projectId: z.string().uuid(),
  repoId: z.string().uuid().nullable(),
  provider: z.enum(SOURCE_REPO_PROVIDERS).nullable(),
  sha: z.string(),
  files: z.array(z.string()),
  skippedPaths: z.array(z.string()),
  recommended: z.array(TiaRecommendedFlowSchema),
  fallbackFlows: z.array(z.string().uuid()),
  unmappedFiles: z.array(z.string()),
  ephemeralReads: z.number().int().nonnegative(),
  bodiesPersisted: z.literal(false),
  /** Phase 5E.6 F-02 — Change Impact apply outcome (compare/TIA still succeed). */
  changeImpactApplied: z.boolean().optional(),
  changeImpactApplyError: z
    .object({
      code: z.literal('CHANGE_IMPACT_APPLY_FAILED'),
      message: z.string().min(1).max(500),
    })
    .optional(),
});
export type CompareCommitResponse = z.infer<typeof CompareCommitResponseSchema>;
